import React, { useState, useEffect } from "react";
import AppLayout from "../../components/AppLayout";
import { FileText, Shield, AlertTriangle, Save, RefreshCw, Bold, Italic, Link as LinkIcon, Image as ImageIcon, List, ListOrdered, Code, Eye } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function LegalDocsEditor() {
  const [activeDoc, setActiveDoc] = useState("terms");
  const [documents, setDocuments] = useState({
    terms: { content: "", last_updated: null },
    privacy: { content: "", last_updated: null },
    takedown: { content: "", last_updated: null }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const docConfig = {
    terms: {
      label: "Terms of Service",
      icon: FileText,
      color: "pink"
    },
    privacy: {
      label: "Privacy Policy",
      icon: Shield,
      color: "emerald"
    },
    takedown: {
      label: "DMCA Takedown",
      icon: AlertTriangle,
      color: "red"
    }
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const [terms, privacy, takedown] = await Promise.all([
        api.get("/api/legal-docs/terms"),
        api.get("/api/legal-docs/privacy"),
        api.get("/api/legal-docs/takedown")
      ]);

      setDocuments({
        terms: terms.data,
        privacy: privacy.data,
        takedown: takedown.data
      });
    } catch (error) {
      console.error("Failed to load documents:", error);
      toast.error("Failed to load legal documents");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const currentDoc = documents[activeDoc];
      
      await api.put(`/api/legal-docs/${activeDoc}`, {
        content: currentDoc.content,
        last_updated: new Date().toISOString().split('T')[0]
      });

      toast.success(`${docConfig[activeDoc].label} saved successfully`);
      await loadDocuments();
    } catch (error) {
      console.error("Failed to save document:", error);
      toast.error("Failed to save document");
    } finally {
      setSaving(false);
    }
  };

  const handleContentChange = (content) => {
    setDocuments(prev => ({
      ...prev,
      [activeDoc]: {
        ...prev[activeDoc],
        content
      }
    }));
  };

  const insertFormatting = (tag, closeTag = null) => {
    const textarea = document.querySelector('textarea');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    const before = textarea.value.substring(0, start);
    const after = textarea.value.substring(end);

    const newText = closeTag 
      ? `${before}<${tag}>${selectedText}</${closeTag}>${after}`
      : `${before}<${tag}>${selectedText}</${tag}>${after}`;

    handleContentChange(newText);
    
    // Set cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + tag.length + 2,
        start + tag.length + 2 + selectedText.length
      );
    }, 0);
  };

  if (loading) {
    return (
      <AppLayout title="Legal Documents Editor" overline="MANAGE LEGAL CONTENT">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-purple-400" />
        </div>
      </AppLayout>
    );
  }

  const ActiveIcon = docConfig[activeDoc].icon;
  const currentDoc = documents[activeDoc];

  return (
    <AppLayout title="Legal Documents Editor" overline="MANAGE LEGAL CONTENT · RICH TEXT EDITING">
      <div className="space-y-6">
        
        {/* Document Selector Tabs */}
        <div className="flex gap-2 border-b border-white/10 pb-4">
          {Object.keys(docConfig).map((docType) => {
            const { label, icon: Icon, color } = docConfig[docType];
            const isActive = activeDoc === docType;
            
            return (
              <button
                key={docType}
                onClick={() => setActiveDoc(docType)}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all
                  ${isActive 
                    ? `bg-${color}-500/20 text-${color}-400 border border-${color}-500/30` 
                    : 'text-zinc-400 hover:text-white hover:bg-white/5'
                  }
                `}
              >
                <Icon size={18} />
                {label}
              </button>
            );
          })}
        </div>

        {/* Editor Card */}
        <div className="bg-zinc-900/50 border border-white/10 rounded-2xl overflow-hidden">
          
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg bg-${docConfig[activeDoc].color}-500/20 border border-${docConfig[activeDoc].color}-500/30`}>
                <ActiveIcon size={24} className={`text-${docConfig[activeDoc].color}-400`} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">
                  {docConfig[activeDoc].label}
                </h2>
                {currentDoc.last_updated && (
                  <p className="text-sm text-zinc-500">
                    Last updated: {currentDoc.last_updated}
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg font-semibold transition-all"
              >
                <Eye size={18} />
                {showPreview ? "Edit" : "Preview"}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:bg-purple-500/50 text-white rounded-lg font-semibold transition-all"
              >
                {saving ? (
                  <>
                    <RefreshCw size={18} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Formatting Toolbar */}
          {!showPreview && (
            <div className="flex items-center gap-1 p-3 border-b border-white/10 bg-black/30 flex-wrap">
              <button
                onClick={() => insertFormatting('strong')}
                className="p-2 hover:bg-white/10 rounded transition"
                title="Bold">
                <Bold size={16} className="text-zinc-400" />
              </button>
              <button
                onClick={() => insertFormatting('em')}
                className="p-2 hover:bg-white/10 rounded transition"
                title="Italic">
                <Italic size={16} className="text-zinc-400" />
              </button>
              <div className="w-px h-6 bg-white/10 mx-1" />
              <button
                onClick={() => insertFormatting('h2')}
                className="px-2 py-1 hover:bg-white/10 rounded transition text-xs font-bold text-zinc-400"
                title="Heading 2">
                H2
              </button>
              <button
                onClick={() => insertFormatting('h3')}
                className="px-2 py-1 hover:bg-white/10 rounded transition text-xs font-bold text-zinc-400"
                title="Heading 3">
                H3
              </button>
              <div className="w-px h-6 bg-white/10 mx-1" />
              <button
                onClick={() => insertFormatting('ul')}
                className="p-2 hover:bg-white/10 rounded transition"
                title="Bullet List">
                <List size={16} className="text-zinc-400" />
              </button>
              <button
                onClick={() => insertFormatting('ol')}
                className="p-2 hover:bg-white/10 rounded transition"
                title="Numbered List">
                <ListOrdered size={16} className="text-zinc-400" />
              </button>
              <div className="w-px h-6 bg-white/10 mx-1" />
              <button
                onClick={() => insertFormatting('a href=""', 'a')}
                className="p-2 hover:bg-white/10 rounded transition"
                title="Link">
                <LinkIcon size={16} className="text-zinc-400" />
              </button>
              <button
                onClick={() => insertFormatting('code')}
                className="p-2 hover:bg-white/10 rounded transition"
                title="Code">
                <Code size={16} className="text-zinc-400" />
              </button>
              <div className="flex-1" />
              <span className="text-xs text-zinc-500 px-2">
                Select text and click formatting button
              </span>
            </div>
          )}

          {/* Content Editor or Preview */}
          <div className="p-6">
            {showPreview ? (
              <div 
                className="prose prose-invert max-w-none min-h-[600px] p-4 rounded-lg bg-black/50 border border-white/10"
                dangerouslySetInnerHTML={{ __html: currentDoc.content || '<p className="text-zinc-500">No content yet</p>' }}
              />
            ) : (
              <textarea
                value={currentDoc.content}
                onChange={(e) => handleContentChange(e.target.value)}
                placeholder={`Enter the content for ${docConfig[activeDoc].label}...

Use HTML for formatting:
- <h2>Heading</h2>
- <p>Paragraph</p>
- <ul><li>List item</li></ul>
- <strong>Bold</strong>
- <em>Italic</em>
- <a href="url">Link</a>`}
                className="w-full h-[600px] bg-black/50 border border-white/10 rounded-lg p-4 text-white font-mono text-sm focus:outline-none focus:border-purple-500/50 resize-none"
              />
            )}
          </div>

          {/* Footer Info */}
          <div className="p-4 bg-black/30 border-t border-white/10">
            <p className="text-xs text-zinc-500">
              💡 Tip: Use the formatting toolbar or write HTML directly. Changes will be reflected on the public legal pages immediately after saving.
            </p>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
